import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import {MAX_PROCEDURE_NAME_LENGTH} from 'procedures/design/ProcedureDesignConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import EditableTextField from 'ui-library/inputs/EditableTextField';

interface ProcedureNameEditorProps {
  name: string;
  onRename: (newValue: string | number) => Promise<void>;
  disableEditsWithTooltip?: string | null;
}

export default function ProcedureNameEditor(props: ProcedureNameEditorProps): JSX.Element {
  const {onRename: onRenameHandler} = props;
  const [nameWhileWaiting, setNameWhileWaiting] = useStateIfMounted(props.name);
  const [isWaiting, setIsWaiting] = useStateIfMounted(false);
  const displayName = isWaiting ? nameWhileWaiting : props.name;
  /**
   * While the onRename callback is running (and probably invoking the API), we use a placeholder value.
   * Otherwise, the name that comes on props will be shown briefly:
   *
   *   - Original name: ABC
   *   - User edits it to: ABCDEF
   *   - User presses ENTER
   *   - Name reverts to ABC while the API runs
   *   - When the API completes, name shows ABCDEF
   */
  const onRename = useCallback(
    async (newValue: string | number): Promise<void> => {
      setIsWaiting(true);
      setNameWhileWaiting(String(newValue));
      await onRenameHandler(newValue);
      setIsWaiting(false);
    },
    [onRenameHandler, setIsWaiting, setNameWhileWaiting]
  );

  return (
    // TODO: BNCH-90284 text field should grow when name gets long
    // TODO: BNCH-90283 tooltip should only be enabled when name overflows, and should not scroll
    <Tooltip isDisabled={displayName === ''} text={displayName} placement={FloatingPlacement.BOTTOM_START}>
      <EditableTextField
        requestChange={onRename}
        value={displayName}
        maxLength={MAX_PROCEDURE_NAME_LENGTH}
        isDisabled={isWaiting}
        disableEditsWithTooltip={props.disableEditsWithTooltip}
        hasFieldActionButtonsOuterMargin={false}
        dataTestAttrs={{component: 'ProcedureNameEditor', element: 'title'}}
        inputClassName={css(styles.nameInput)}
      />
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  nameInput: {
    width: '400px',
  },
});
